(() => {
    // Prevent multiple executions
    if (window.__customDeliveryMessageLoaded) return;
    window.__customDeliveryMessageLoaded = true;

    const WRAPPER_SELECTOR = '.custom-delivery-message-wraper';
    const NEXT_DAY_BADGE_SELECTOR = '[data-next-day-badge]';
    const ETA_TEXT_SELECTOR = '[data-delivery-eta-text]';
    const GUARANTEE_SELECTOR = '.money-back-guarantee-message';
    const GUARANTEE_TEXT_SELECTOR = '[data-guarantee-text]';

    // Inline copies of custom-icon-truck.svg and icon-certified.svg. This markup is
    // built in JS, where the Liquid inline_asset_content filter is not available.
    const TRUCK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 4 20 16" width="15" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;" aria-hidden="true"><path d="M3 7h10v9H3z"/><path d="M13 10h5l3 3v3h-8z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17" cy="17" r="1.6"/></svg>';
    const CERTIFIED_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;" aria-hidden="true"><path d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5 12 21.04l3.4 1.46 1.89-3.19 3.61-.82-.34-3.69z"/><path d="M7.5 12.5l3 3 6-6"/></svg>';

    // Both boxes share the same pill shape; only the palette and the icon change.
    const BOX_STYLE = "border-radius: 4px; font-family: 'Inter'; font-weight: normal; font-size: 12px; line-height: 18px; text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; box-sizing: border-box;";
    const ETA_BOX_STYLE = `background: #EE5C4F; color: #fff; padding: 2px 10px; margin: 8px 0px 0px; ${BOX_STYLE}`;
    const GUARANTEE_BOX_STYLE = `background: #f5f5f5; color: #303030; padding: 6px 10px; margin: 4px 0px 0px; ${BOX_STYLE}`;

    const guaranteeMarkup = (message) =>
        `<div class="money-back-guarantee-message" style="${GUARANTEE_BOX_STYLE}">${CERTIFIED_ICON}<span data-guarantee-text>${message}</span></div>`;

    // Delivery date maths live in the shared delivery-date.js module (single
    // source of truth shared with the cart drawer). Load order is guaranteed by
    // main-product.liquid, which loads delivery-date.js right before this file.
    const delivery = window.OGKicksDelivery;
    if (!delivery) {
        console.warn('[Delivery Message] OGKicksDelivery not loaded; delivery-date.js must load first');
        return;
    }

    const { calculateDeliveryMessage, getGuaranteeMessage, isNextDayProvider } = delivery;

    /**
     * Keep the "Next day delivery" pill in sync with the selected variant. Liquid
     * renders it for the default variant, so it has to follow every size change:
     * a next-day size and a 3 to 5 day size can live on the same product.
     */
    const updateNextDayBadge = (provider) => {
        const badge = document.querySelector(NEXT_DAY_BADGE_SELECTOR);
        if (!badge) return;
        badge.classList.toggle('hidden', !isNextDayProvider(provider));
    };

    /**
     * Get current variant provider from product data
     */
    const getSelectedVariantProvider = () => {
        console.log('[Delivery Message] Getting selected variant provider...');

        // Try to get from selected variant input with data attribute
        // Look for any radio input that has data-variant-provider attribute and is checked
        const variantInput = document.querySelector('input[type="radio"][data-variant-provider]:checked');
        if (variantInput) {
            const provider = variantInput.getAttribute('data-variant-provider');
            console.log('[Delivery Message] Provider from checked input:', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: get first available option if none is checked yet
        const firstInput = document.querySelector('input[type="radio"][data-variant-provider]');
        if (firstInput) {
            const provider = firstInput.getAttribute('data-variant-provider');
            console.log('[Delivery Message] Provider from first input:', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: for products with only default variant (no variant picker rendered)
        // The provider is server-rendered as data-default-variant-provider on the product-info element
        const productInfoEl = document.querySelector('product-info[data-default-variant-provider]');
        if (productInfoEl) {
            const provider = productInfoEl.getAttribute('data-default-variant-provider');
            console.log('[Delivery Message] Provider from product-info (default variant):', provider);
            if (provider) {
                return provider;
            }
        }

        // Fallback: try window.product
        if (window.product && window.product.selected_or_first_available_variant) {
            const variant = window.product.selected_or_first_available_variant;
            const provider = variant?.metafields?.custom?.variant_location_provider || null;
            console.log('[Delivery Message] Provider from window.product:', provider);
            console.log('[Delivery Message] Variant data:', variant);
            return provider;
        }

        // Last fallback: search in window.product.variants
        if (!variantInput) {
            console.log('[Delivery Message] No variant input found');
            return null;
        }

        const variantId = parseInt(variantInput.value);
        console.log('[Delivery Message] Variant ID from input:', variantId);

        if (window.product && window.product.variants) {
            const variant = window.product.variants.find(v => v.id === variantId);
            const provider = variant?.metafields?.custom?.variant_location_provider || null;
            console.log('[Delivery Message] Provider from variants array:', provider);
            console.log('[Delivery Message] Variant data:', variant);
            return provider;
        }

        console.log('[Delivery Message] No provider found');
        return null;
    };

    /**
     * Update the delivery message in the DOM
     */
    const updateDeliveryMessage = () => {
        console.log('[Delivery Message] Updating delivery message...');

        const messageElement = document.querySelector(`[data-custom-delivery-message="true"] ${ETA_TEXT_SELECTOR}`);
        const guaranteeElement = document.querySelector(`[data-custom-delivery-message="true"] ${GUARANTEE_SELECTOR}`);

        const provider = getSelectedVariantProvider();
        updateNextDayBadge(provider);

        if (!messageElement) {
            console.log('[Delivery Message] Message element not found');
            insertMessage();
            return;
        }

        const message = calculateDeliveryMessage(provider);
        const guaranteeMessage = getGuaranteeMessage(provider);
        const messageContainer = messageElement.closest('[data-custom-delivery-message="true"]');

        if (!message) {
            console.log('[Delivery Message] No delivery date available, hiding message');
            if (messageContainer) {
                messageContainer.remove();
            }
            return;
        }

        console.log('[Delivery Message] Calculated message:', message);
        messageElement.innerHTML = message;

        // Update or create guarantee message
        if (guaranteeMessage) {
            if (guaranteeElement) {
                guaranteeElement.querySelector(GUARANTEE_TEXT_SELECTOR).innerHTML = guaranteeMessage;
                guaranteeElement.style.display = 'flex';
            } else {
                // Create guarantee element if it doesn't exist. The ETA text lives inside
                // the red box, so the sibling has to be added after that box, not after the text.
                messageElement.parentElement.insertAdjacentHTML('afterend', guaranteeMarkup(guaranteeMessage));
            }
        } else if (guaranteeElement) {
            guaranteeElement.style.display = 'none';
        }

        console.log('[Delivery Message] Message updated in DOM');
    };

    /**
     * Insert the message component into the DOM
     */
    const insertMessage = () => {
        console.log('[Delivery Message] insertMessage called');

        // Check if already inserted globally - remove any existing ones first
        const existingMessages = document.querySelectorAll('[data-custom-delivery-message="true"]');
        if (existingMessages.length > 0) {
            console.log('[Delivery Message] Found existing messages, removing:', existingMessages.length);
            existingMessages.forEach(msg => msg.remove());
        }

        const wrapper = document.querySelector(WRAPPER_SELECTOR);
        if (!wrapper) {
            console.log('[Delivery Message] Wrapper not found');
            return;
        }

        console.log('[Delivery Message] Wrapper found:', wrapper);

        const provider = getSelectedVariantProvider();
        const message = calculateDeliveryMessage(provider);
        const guaranteeMessage = getGuaranteeMessage(provider);

        if (!message) {
            console.log('[Delivery Message] No delivery date available, not inserting message');
            return;
        }

        const markup = `
        <div data-custom-delivery-message="true" class="shopify-block shopify-app-block">
          <div class="custom-delivery-message-block">
            <div class="custom-delivery-message">
              <div class="estimator-instock-message" style="display: block;">
                <div style="${ETA_BOX_STYLE}">${TRUCK_ICON}<span data-delivery-eta-text>${message}</span></div>
                ${guaranteeMessage ? guaranteeMarkup(guaranteeMessage) : ''}
              </div>
            </div>
          </div>
        </div>
      `.trim();

        wrapper.insertAdjacentHTML('beforeend', markup);
        console.log('[Delivery Message] Message inserted into DOM');
    };

    /**
     * Listen for variant changes
     */
    const setupVariantChangeListener = () => {
        console.log('[Delivery Message] Setting up variant change listeners...');

        // Listen for Shopify variant change events
        document.addEventListener('variant:change', () => {
            console.log('[Delivery Message] variant:change event detected');
            updateDeliveryMessage();
        });

        // Also listen for radio button changes (variant picker)
        document.addEventListener('change', (e) => {
            if (e.target.matches('input[type="radio"][data-variant-provider]')) {
                console.log('[Delivery Message] Input change detected:', e.target);
                console.log('[Delivery Message] Selected provider:', e.target.getAttribute('data-variant-provider'));
                setTimeout(updateDeliveryMessage, 100);
            }
        });

        console.log('[Delivery Message] Listeners set up successfully');
    };

    /**
     * Initialize
     */
    const init = () => {
        updateNextDayBadge(getSelectedVariantProvider());
        insertMessage();
        setupVariantChangeListener();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
